import { useSelector, useDispatch } from 'react-redux';
import { updateItemQuantity, deleteFromCart, clearCart } from '../../store/cartSlice';
import { useNavigate } from 'react-router-dom';
import { showConfirm } from '../../utils/sweetAlert';
import { getDisplayImageUrl } from '../../utils/imageUtils';

const Cart = () => {
    const cart = useSelector((state) => state.cart.items);
    const total = useSelector((state) => state.cart.total);
    const dispatch = useDispatch();
    const navigate = useNavigate();

    const getImageUrl = (imageUrl) => getDisplayImageUrl(imageUrl);

    // El precio unitario se recalcula automáticamente dentro del reducer, en base
    // a la cantidad TOTAL de la categoría en el carrito (no solo la de este item),
    // para que el descuento por cantidad aplique aunque se combinen productos
    // distintos de la misma categoría.
    const handleIncrease = (item) => {
        dispatch(updateItemQuantity({ item, quantity: item.quantity + 1 }));
    };

    const handleDecrease = (item) => {
        dispatch(updateItemQuantity({ item, quantity: item.quantity - 1 }));
    };

    const handleCheckout = () => {
        navigate('/checkout'); 
    };

    const handleClearCart = async () => {
        const result = await showConfirm(
            '¿Vaciar carrito?',
            'Esta acción eliminará todos los productos del carrito',
            'Sí, vaciar',
            'Cancelar'
        );
        if (result) {
            dispatch(clearCart());
        }
    };

    const handleDeleteItem = async (item) => {
        const result = await showConfirm(
            '¿Eliminar producto?',
            `¿Estás seguro de que deseas eliminar "${item.name}" del carrito?`,
            'Sí, eliminar',
            'Cancelar'
        );
        if (result) {
            dispatch(deleteFromCart(item));
        }
    };

    if (cart.length === 0) {
        return (
            <div className="container wrapper mt-5">
                <div className="text-center py-5">
                    <i className="fa-solid fa-cart-shopping" style={{ fontSize: '4rem', color: '#ccc', marginBottom: '20px' }}></i>
                    <h2 className="mb-3">Tu carrito está vacío</h2>
                    <p className="text-muted mb-4">¡Agrega algunos productos increíbles!</p>
                    <button 
                        className="add-to-cart-btn px-5 py-3 shadow-sm"
                        style={{ fontSize: '1rem', letterSpacing: '1px', textTransform: 'uppercase', fontWeight: 'bold', margin: '0 auto' }}
                        onClick={() => navigate('/tienda')}
                    >
                        <i className="fa-solid fa-store me-2"></i>
                        Ir a la tienda
                    </button>
                </div>
            </div>
        );
    }

    return (
        <div className="container wrapper mt-5 mb-5">
            <div className="d-flex align-items-center justify-content-between mb-4">
                <h1 className="h3 mb-0 fw-bold">
                    <i className="fa-solid fa-cart-shopping me-3 text-success"></i>
                    Carrito de Compras
                </h1>
                <span className="badge bg-light text-dark border px-3 py-2 rounded-pill">
                    {cart.reduce((acc, item) => acc + item.quantity, 0)} productos
                </span>
            </div>

            <div className="row g-4">
                {/* Columna Izquierda: Lista de Productos */}
                <div className="col-lg-8">
                    <div className="cart-items-container d-flex flex-column gap-3">
                        {cart.map((item) => (
                            <div key={item.cartItemId || `${item._id}-${item.selectedVariant?.sku || 'no-variant'}`} className="card border-0 shadow-sm rounded-4 overflow-hidden">
                                <div className="card-body p-3">
                                    <div className="row align-items-center">
                                        {/* Imagen */}
                                        <div className="col-3 col-md-2">
                                            <div className="position-relative ratio ratio-1x1 rounded-3 overflow-hidden bg-light">
                                                <img 
                                                    src={getImageUrl(item.selectedVariant?.images?.[0] || item.images?.[0] || item.image)} 
                                                    alt={item.name}
                                                    className="object-fit-cover"
                                                    style={{ width: '100%', height: '100%' }}
                                                    onError={(e) => { e.target.src = '/placeholder-image.svg'; }}
                                                />
                                            </div>
                                        </div>

                                        {/* Detalles */}
                                        <div className="col-9 col-md-5">
                                            <h5 className="mb-1 text-truncate fw-bold" title={item.name}>{item.name}</h5>
                                            <div className="d-flex flex-wrap gap-2 mb-1">
                                                {item.isCustomizable && (
                                                    <span className="badge" style={{ background: '#1f8e38', color: '#fff', fontSize: '0.7rem' }}>
                                                        <i className="fas fa-wand-magic-sparkles me-1"></i>Personalizado IA
                                                    </span>
                                                )}
                                                {item.selectedVariant ? (
                                                    <>
                                                        <span className="badge bg-light text-dark border">{item.selectedVariant.size}</span>
                                                        <span className="badge bg-light text-dark border">{item.selectedVariant.color}</span>
                                                    </>
                                                ) : item.selectedSize ? (
                                                    <span className="badge bg-light text-dark border">{item.selectedSize}</span>
                                                ) : item.variantInfo && (
                                                    <span className="text-muted small">{item.variantInfo}</span>
                                                )}
                                            </div>
                                            {/* Detalles de personalización */}
                                            {item.isCustomizable && (
                                                <div className="mt-1">
                                                    {item.customImage?.url && (
                                                        <div className="d-flex align-items-center gap-2 mb-1">
                                                            <img src={getImageUrl(item.customImage.url)} alt="Tu foto"
                                                                style={{ width: '36px', height: '36px', borderRadius: '6px', objectFit: 'cover', border: '1px solid #1f8e38' }}
                                                                onError={(e) => { e.target.style.display = 'none'; }} />
                                                            <small className="text-muted">Tu foto adjunta</small>
                                                        </div>
                                                    )}
                                                    {item.customFieldValues && Object.keys(item.customFieldValues).length > 0 && (
                                                        <div style={{ fontSize: '0.75rem', color: '#666' }}>
                                                            {Object.entries(item.customFieldValues).map(([k, v]) => (
                                                                <span key={k} className="me-2"><strong>{k}:</strong> {v}</span>
                                                            ))}
                                                        </div>
                                                    )}
                                                </div>
                                            )}
                                            <div className="text-success fw-bold d-md-none mt-1">
                                                ${item.price * item.quantity}
                                            </div>
                                        </div>

                                        {/* Cantidad y Precio (Desktop) */}
                                        <div className="col-12 col-md-5 mt-3 mt-md-0">
                                            <div className="d-flex align-items-center justify-content-between justify-content-md-end gap-4">
                                                {item.isCustomizable ? (
                                                    <div className="text-center" style={{ minWidth: '110px' }}>
                                                        <span className="form-control text-center bg-light border-0 fw-bold" style={{ width: '110px' }}>
                                                            {item.quantity}
                                                        </span>
                                                    </div>
                                                ) : (
                                                <div className="input-group input-group-sm" style={{ width: '110px' }}>
                                                    <button 
                                                        className="btn btn-outline-secondary" 
                                                        onClick={() => handleDecrease(item)}
                                                    >
                                                        <i className="fa-solid fa-minus"></i>
                                                    </button>
                                                    <span className="form-control text-center bg-white border-secondary border-start-0 border-end-0">
                                                        {item.quantity}
                                                    </span>
                                                    <button 
                                                        className="btn btn-outline-secondary"
                                                        onClick={() => handleIncrease(item)}
                                                    >
                                                        <i className="fa-solid fa-plus"></i>
                                                    </button>
                                                </div>
                                                )}

                                                <div className="text-end d-none d-md-block" style={{ minWidth: '80px' }}>
                                                    <div className="fw-bold fs-5">${item.price * item.quantity}</div>
                                                    {item.quantity > 1 && (
                                                        <small className="text-muted">${item.price} c/u</small>
                                                    )}
                                                </div>

                                                <button
                                                    className="btn btn-link text-danger p-0"
                                                    onClick={() => handleDeleteItem(item)}
                                                    title="Eliminar"
                                                >
                                                    <i className="fa-solid fa-trash-can fa-lg"></i>
                                                </button>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>
                    
                    <div className="d-flex justify-content-between align-items-center mt-4">
                        <button 
                            className="btn btn-link text-decoration-none text-muted px-0"
                            onClick={() => navigate('/tienda')}
                        >
                            <i className="fa-solid fa-arrow-left me-2"></i>
                            Seguir comprando
                        </button>
                        
                        <button 
                            className="btn btn-outline-danger btn-sm rounded-pill px-3"
                            onClick={handleClearCart}
                        >
                            <i className="fa-solid fa-trash me-2"></i>
                            Vaciar carrito
                        </button>
                    </div>
                </div>

                {/* Columna Derecha: Resumen (Sticky) */}
                <div className="col-lg-4">
                    <div className="card border-0 shadow rounded-4 sticky-top" style={{ top: '2rem', zIndex: 10 }}>
                        <div className="card-body p-4">
                            <h5 className="card-title fw-bold mb-4">Resumen del pedido</h5>
                            
                            <div className="d-flex justify-content-between mb-3">
                                <span className="text-muted">Subtotal</span>
                                <span className="fw-bold">${total}</span>
                            </div>
                            
                            <div className="d-flex justify-content-between mb-3">
                                <span className="text-muted">Envío</span>
                                <span className="text-success fw-bold">Calculado en el checkout</span>
                            </div>

                            <hr className="my-3" />

                            <div className="d-flex justify-content-between align-items-center mb-4">
                                <span className="fs-5 fw-bold">Total</span>
                                <span className="fs-4 fw-bold text-success">${total}</span>
                            </div>

                            <button 
                                className="add-to-cart-btn w-100 py-3 shadow-sm mb-3"
                                style={{ fontSize: '1rem', letterSpacing: '1px', textTransform: 'uppercase', fontWeight: 'bold' }}
                                onClick={handleCheckout}
                            >
                                <i className="fa-solid fa-cart-shopping me-2"></i>
                                PROCEDER AL CHECKOUT
                            </button>

                            <div className="text-center">
                                <div className="d-flex justify-content-center gap-3 text-muted mb-2" style={{ fontSize: '1.5rem' }}>
                                    <i className="fa-brands fa-cc-visa"></i>
                                    <i className="fa-brands fa-cc-mastercard"></i>
                                    <i className="fa-brands fa-cc-amex"></i>
                                    <i className="fa-solid fa-shield-halved"></i>
                                </div>
                                <small className="text-muted">
                                    <i className="fa-solid fa-lock me-1"></i>
                                    Pago 100% seguro y encriptado
                                </small>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default Cart;
